/* eslint-disable import/extensions */
/* eslint-disable import/no-unresolved */


import React from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import Promise from 'bluebird';
import { compose, extend, find } from 'underscore';
import dataContextManager from '@bingads-webui-static-legacy/summary/SummaryDataContextManager';
import { withDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { QueryParam } from '@bingads-webui-react/scope-service';
import { withI18nConsumer } from '@bingads-webui-react/i18n-context';
import { FAC_FEATURE_ID } from '@bingads-webui-campaign/recommendation-core';
import { withSiteMap } from '@bingads-webui-campaign-react/with-site-map';
import { ProgressiveLoadingBlock } from '@bingads-webui-campaign-react/shell-ui-common';
import { BackboneInstanceWrapper } from '@bingads-webui-react/backbone-instance-wrapper';
import { perfMarkerPropTypes } from '@bingads-webui/performance-marker';
import { withCcJsData } from '@bingads-webui-cc-react/js-data-rest';
import { Constants as CCUIConstants } from '@bingads-webui-clientcenter/entity-utils';
import { getUrlBuilders } from '@bingads-webui-clientcenter/url-builder-base';
import { notificationApi } from '@bingads-webui-react/notification-center';
import { fetchInitData, getPendingImportStatusPromise } from './repository';
import { loadLegacyDeps, createCommonDeps } from './utils';
import { getGoogleImportStyle } from './styles/google-import-page-style';

const FAC_FEATUREID =
  parseInt(FAC_FEATURE_ID.GOOGLE_IMPORT, 10);
const FAC_STATUS_NOT_IN_PROGRESS = CCUIConstants.RecommendationAdoptionStatus.NotInProgess;
const FAC_STATUS_ACTIVE = CCUIConstants.RecommendationAdoptionStatus.Active;

class GoogleImport extends React.PureComponent {
  constructor(props) {
    super(props);
    this.perfMarker = props.perfMarker;
    this.initialize(props);
    this.state = {
      importId: props.scope.values.importId,
    };
  }

  static getDerivedStateFromProps(nextProps, prevState) {
    if (nextProps.scope.values.importId !== prevState.importId) {
      return {
        importId: nextProps.scope.values.importId,
      };
    }
    return null;
  }

  componentWillUnmount() {
    // call google import page remove function
    if (this.googleImportPage) {
      this.googleImportPage.remove();
    }
  }

  initialize(props) {
    this.perfMarker.willFetchData();
    const fetchInitDataPromise = fetchInitData(props.scope.values, true);
    // don't take reject param, if coupon call fails, we will just not show the offer
    const getCouponPromise = new Promise((resolve) => {
      if (props.scope.values.aid) {
        resolve(props.ccJsData.FeatureAdoptionCoupon
          .findAll({ aid: props.scope.values.aid }));
      }
      resolve(null);
    })
      // if call fails, eat the error to avoid blocking workflow
      .then(coupons =>
        find(
          coupons,
          coupon =>
            coupon.FeatureId === FAC_FEATUREID &&
              (coupon.Status === FAC_STATUS_NOT_IN_PROGRESS || coupon.Status === FAC_STATUS_ACTIVE)
        )).catch(() => null);

    return React.lazy(() => Promise.all([
      fetchInitDataPromise,
      getPendingImportStatusPromise(),
      getCouponPromise,
    ]).then(([data, importStatus, coupon]) => {
      const deps = this.createDeps(data, props, importStatus, coupon);
      return loadLegacyDeps(props).then(spaFactory => spaFactory.initGoogleImportPage(deps));
    })
      .then(view => view.render())
      .then((instance) => {
        this.googleImportPage = instance;
        ReactDOM.render(
          <BackboneInstanceWrapper
            instance={instance}
          />,
          document.getElementById('googleImportPageViewMount')
        );
        instance.startPollingAfterContinueImport();

        return {
          default: () => null,
        };
      }));
  }

  createDeps(data, props, importStatus, coupon) {
    const {
      importId,
      taskItemId,
      opportunityId,
      source,
      opportunityType,
    } = props.scope.values;
    const currency = props.serverContext.CurrentAccount && props.serverContext.CurrentAccount.CurrencyCode;

    const deps = createCommonDeps({
      ...props,
      data,
    });

    const { BillingUrlBuilder } = getUrlBuilders({
      ccBaseUrl: props.serverContext.Urls.ClientCenterBaseUrl,
      aidFetcher: () => props.serverContext.CurrentAccount.Id,
      cidFetcher: () => props.serverContext.CurrentCustomer.Id,
      uidFetcher: () => props.serverContext.CurrentUser.Id,
    });

    extend(
      deps,
      {
        currency,
        importStatus,
        importId,
        taskItemId,
        opportunityId,
        opportunitySource: source,
        opportunityType,
        uxMode: 'main',
        perfMarker: this.perfMarker,
        ccJsData: props.ccJsData,
        showAlertHandler: ({
          title,
          message,
          level,
          action,
        }) => {
          let constructedMessage = message;

          if (title) {
            constructedMessage = `<strong>${title}</strong> ${constructedMessage}`;
          }
          if (action) {
            const openingAnchor = `<a href="${action.href}" target="_blank" >`;

            constructedMessage = `${constructedMessage} ${openingAnchor}${action.message}</a>`;
          }
          notificationApi.add({
            severity: level,
            title,
            message: constructedMessage,
            target: 'FloatingBanner',
          });
        },
        isBillToCustomer: data.isBillToCustomer,
        couponDetailsUrl: new BillingUrlBuilder({ isShortUrl: false }).action('coupondetails').url,
        dataContextManager,
      },
      coupon && { coupon }
    );

    return deps;
  }

  render() {
    this.GoogleImportPageView = this.initialize(this.props);
    return (
      <React.Suspense fallback={<ProgressiveLoadingBlock />}>
        <div className={classNames(this.props.classes.pageContainer, this.props.classes.googleImportPage, 'google-import-page-container')}>
          <this.GoogleImportPageView />
          <div id="googleImportPageViewMount" />
        </div>
      </React.Suspense>
    );
  }
}

GoogleImport.propTypes = {
  serverContext: PropTypes.shape({
    CurrentAccount: PropTypes.object.isRequired,
    CurrentCustomer: PropTypes.object.isRequired,
  }).isRequired,
  classes: PropTypes.shape({
    pageContainer: PropTypes.string.isRequired,
    googleImportPage: PropTypes.string.isRequired,
  }).isRequired,
  i18n: PropTypes.shape({
    getString: PropTypes.func,
  }).isRequired,
  permissions: PropTypes.shape({
    IsUserContentAllowed: PropTypes.bool.isRequired,
  }).isRequired,
  perfMarker: perfMarkerPropTypes.isRequired,
  scope: PropTypes.instanceOf(QueryParam).isRequired,
};

export const StyledGoogleImport = compose(
  withI18nConsumer,
  withCcJsData,
  WrappedComponent => withSiteMap(withDefaultStyles(WrappedComponent, getGoogleImportStyle))
)(GoogleImport);
